import { Inject, Injectable } from '@angular/core';
import { firstValueFrom, Subject } from 'rxjs';
import { CompanyId } from '../../../../../common/dto/company.dto';
import {
  SUBSCRIPTION_CHECKOUT_DATA,
  SubscriptionId,
  SubscriptionPeriod,
  SubscriptionType,
} from '../../../../../common/dto/subscription.dto';
import { loadScriptAsync } from '../../../../../common/utils/window.utils';
import { CompaniesService } from './companies.service';
import { WINDOW } from './injection-tokens';

const CLOSE_EVENTS = new Set([
  'close',
  'Checkout.Success',
]);

@Injectable()
export class LemonSqueezyService {
  private _isInitialized = false;

  private _isOpen = false;

  private _closed = new Subject<void>();

  constructor(
    @Inject(WINDOW) private _window: Window,
    private _companiesService: CompaniesService,
  ) {
  }

  public async startCheckoutAsync(subscriptionType: SubscriptionType, period: SubscriptionPeriod, companyId: CompanyId): Promise<void> {
    const subscriptionData = SUBSCRIPTION_CHECKOUT_DATA[subscriptionType][period];

    if (!subscriptionData) {
      throw new Error('Unknown subscription type');
    }

    const [companyData] = await Promise.all([
      this._companiesService.getCompanyDataAsync(),
      this._ensureLemonJSAsync(),
    ]);

    const url = new URL(`https://amill.lemonsqueezy.com/buy/${ subscriptionData.LSCheckoutId }`);

    url.searchParams.set('ember', '1');
    url.searchParams.set('media', '0');
    url.searchParams.set('logo', '0');
    url.searchParams.set('description', '0');

    url.searchParams.set('checkout[custom][company_id]', companyId);
    url.searchParams.set('checkout[email]', companyData.email);

    await this._openUrlAsync(url.toString());
  }

  public async manageSubscriptionAsync(subscriptionId: SubscriptionId) {
    const [resp] = await Promise.all([
      this._companiesService.getSubscriptionManageUrlAsync(subscriptionId),
      this._ensureLemonJSAsync(),
    ]);

    await this._openUrlAsync(resp.link);
  }

  private async _openUrlAsync(url: string) {
    this._isOpen = true;

    (<any>this._window).LemonSqueezy.Url.Open(url);

    await firstValueFrom(this._closed);
  }

  private async _ensureLemonJSAsync() {
    if (this._isInitialized) {
      return;
    }

    await loadScriptAsync(this._window, 'https://app.lemonsqueezy.com/js/lemon.js');

    (<any>this._window).createLemonSqueezy();

    (<any>this._window).LemonSqueezy.Setup({
      eventHandler: async (event: any) => {
        if (this._isOpen && typeof event === 'string' && CLOSE_EVENTS.has(event)) {
          this._closed.next();

          this._isOpen = false;
        }
      },
    });

    this._isInitialized = true;
  }
}
